import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Divider,
  FileInput,
  Group,
  MultiSelect,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconTrash } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Client, User } from 'src/admin/users/types';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Company, useCompanies } from 'src/secret-menu/hooks/useCompanies';
import {
  CleanTranscriptResult,
  FamilyMember,
  Guest,
  ParticipantNamesInput,
  TranscriptSegment,
  useProcessTranscript,
} from './useCleanTranscript';
import {
  AdminMeetingNoteContent,
  AdminTemplate,
  useGenerateMeetingNote,
} from './useGenerateMeetingNote';
import { useCleanupRun } from './useCleanupRun';

const AFTERNOON_TENANT_NAME = 'afternoon';
const MAX_NOTE_POLLS = 100;

function usePersistedState<T>(key: string, defaultValue: T) {
  return useLocalStorage<T>({
    key: `transcript-tester:${key}`,
    defaultValue,
    getInitialValueInEffect: false,
  });
}

function TranscriptView({ segments }: { segments: TranscriptSegment[] }) {
  if (segments.length === 0) {
    return (
      <Text size="textSm" c="dimmed" py="md">
        No segments.
      </Text>
    );
  }
  return (
    <Stack gap="sm" py="sm">
      {segments.map((segment, index) => (
        <Group key={index} gap="sm" align="flex-start" wrap="nowrap">
          <Stack gap={0} style={{ minWidth: 170 }}>
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
              {segment.speaker || 'Unknown'}:
            </Text>
            {segment.startTime && (
              <Text size="textXs" c="dimmed">
                {segment.startTime}
                {segment.endTime ? `–${segment.endTime}` : ''}
              </Text>
            )}
          </Stack>
          <Text size="textSm" c="gray.9">
            {segment.content}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

export default function TranscriptTesterTab() {
  const { companies } = useCompanies();
  const afternoon = (companies ?? []).find(company => company.tenantName === AFTERNOON_TENANT_NAME);
  const afternoonId = afternoon?.id ?? null;

  const [selectedClientIds, setSelectedClientIds, clearSelectedClientIds] = usePersistedState<
    string[]
  >('clientIds', []);
  const [guests, setGuests, clearGuests] = usePersistedState<Guest[]>('guests', []);
  const [emails, setEmails, clearEmails] = usePersistedState<string[]>('emails', []);
  const [familyMembers, setFamilyMembers, clearFamilyMembers] = usePersistedState<FamilyMember[]>(
    'familyMembers',
    [],
  );
  const [result, setResult, clearResult] = usePersistedState<CleanTranscriptResult | null>(
    'result',
    null,
  );
  const [templateCompanyId, setTemplateCompanyId, clearTemplateCompanyId] = usePersistedState<
    string | null
  >('templateCompanyId', null);
  const [selectedTemplateId, setSelectedTemplateId, clearSelectedTemplateId] = usePersistedState<
    string | null
  >('templateId', null);
  const [additionalInstructions, setAdditionalInstructions, clearAdditionalInstructions] =
    usePersistedState<string>('additionalInstructions', '');
  const [generatedNoteId, setGeneratedNoteId, clearGeneratedNoteId] = usePersistedState<
    string | null
  >('generatedNoteId', null);

  const [audioFile, setAudioFile] = React.useState<File | null>(null);
  const [guestName, setGuestName] = React.useState('');
  const [guestDescription, setGuestDescription] = React.useState('');
  const [emailInput, setEmailInput] = React.useState('');
  const [familyName, setFamilyName] = React.useState('');
  const [familyRole, setFamilyRole] = React.useState('');

  const { process, isProcessing } = useProcessTranscript();
  const { generate, isGenerating } = useGenerateMeetingNote();
  const { cleanup, isCleaning } = useCleanupRun();

  const clearAll = () => {
    clearSelectedClientIds();
    clearGuests();
    clearEmails();
    clearFamilyMembers();
    clearResult();
    clearTemplateCompanyId();
    clearSelectedTemplateId();
    clearAdditionalInstructions();
    clearGeneratedNoteId();
    setAudioFile(null);
    setGuestName('');
    setGuestDescription('');
    setEmailInput('');
    setFamilyName('');
    setFamilyRole('');
  };

  const handleReset = async () => {
    if (afternoonId) {
      try {
        await cleanup(afternoonId);
      } catch (error) {
        // Keep the UI state so Reset can be retried rather than leaving the test data behind.
        notifyAndLogError(
          'Cleanup failed',
          'Could not delete the test data. Try Reset again.',
          error,
        );
        return;
      }
    }
    clearAll();
  };

  const { data: clients } = useQuery<Client[]>({
    queryKey: ['admin', 'companies', afternoonId, 'clients'],
    enabled: !!afternoonId,
  });
  const { data: users } = useQuery<User[]>({
    queryKey: ['admin', 'companies', afternoonId, 'users'],
    enabled: !!afternoonId,
  });
  const { data: templates } = useQuery<AdminTemplate[]>({
    queryKey: ['admin', 'companies', templateCompanyId, 'templates'],
    enabled: !!templateCompanyId,
  });
  const { data: generatedNote } = useQuery<AdminMeetingNoteContent>({
    queryKey: ['admin', 'companies', afternoonId, 'meeting-notes', generatedNoteId, 'content'],
    enabled: !!afternoonId && !!generatedNoteId,
    refetchInterval: query => {
      const note = query.state.data;
      const done = note?.markdownContent || note?.status === 'SUCCESS' || note?.status === 'FAILED';
      if (done || query.state.dataUpdateCount >= MAX_NOTE_POLLS) return false;
      return 3000;
    },
  });
  const noteFailed = generatedNote?.status === 'FAILED';
  const noteGenerating = !!generatedNoteId && !generatedNote?.markdownContent && !noteFailed;

  let notePanelContent = (
    <Text size="textSm" c="dimmed" py="md">
      Generating meeting note…
    </Text>
  );
  if (generatedNote?.markdownContent) {
    notePanelContent = <MarkdownView value={generatedNote.markdownContent} />;
  } else if (noteFailed) {
    notePanelContent = (
      <Text size="textSm" c="red" py="md">
        Meeting note generation failed.
      </Text>
    );
  }

  const selectedClients = selectedClientIds
    .map(id => (clients ?? []).find(client => client.id === id))
    .filter((client): client is Client => client != null);
  const primaryClient = selectedClients[0];
  const advisor = (users ?? []).find(user => user.id === primaryClient?.advisorUserId);
  const advisorName = advisor ? `${advisor.firstName} ${advisor.lastName}` : null;

  const meetingNoteTemplates = (templates ?? []).filter(
    template => template.templateType === 'MEETING_NOTE',
  );
  const sortedCompanies = [...(companies ?? [])].sort((a: Company, b: Company) =>
    a.name.localeCompare(b.name),
  );

  const participants: ParticipantNamesInput = {
    advisorNames: advisorName ? [advisorName] : [],
    clientNames: selectedClients.map(client => `${client.firstName} ${client.lastName}`),
    guests,
    unmatchedAttendeeEmails: emails,
    familyMembers,
    clientIds: selectedClients.map(client => client.id),
  };

  const addGuest = () => {
    if (!guestName.trim()) return;
    setGuests(current => [
      ...current,
      { name: guestName.trim(), description: guestDescription.trim() || undefined },
    ]);
    setGuestName('');
    setGuestDescription('');
  };

  const addEmail = () => {
    if (!emailInput.trim()) return;
    setEmails(current => [...current, emailInput.trim()]);
    setEmailInput('');
  };

  const addFamilyMember = () => {
    if (!familyName.trim()) return;
    setFamilyMembers(current => [
      ...current,
      { name: familyName.trim(), role: familyRole.trim() || undefined },
    ]);
    setFamilyName('');
    setFamilyRole('');
  };

  const handleProcess = async () => {
    if (!afternoonId || !audioFile) return;
    try {
      const { data } = await process({ companyId: afternoonId, audio: audioFile, participants });
      setResult(data);
      setGeneratedNoteId(null);
    } catch (error) {
      notifyAndLogError('Processing failed', 'Could not process the uploaded recording.', error);
    }
  };

  const canGenerate =
    !!afternoonId &&
    !!result &&
    !!templateCompanyId &&
    !!selectedTemplateId &&
    !!selectedClientIds[0];

  const handleGenerate = async () => {
    if (
      !afternoonId ||
      !result ||
      !templateCompanyId ||
      !selectedTemplateId ||
      !selectedClientIds[0]
    ) {
      return;
    }
    try {
      const { data } = await generate({
        destinationCompanyId: afternoonId,
        sourceCompanyId: templateCompanyId,
        templateId: selectedTemplateId,
        clientId: selectedClientIds[0],
        dataUploadFileId: result.dataUploadFileId,
        additionalInstructions: additionalInstructions.trim() || undefined,
      });
      setGeneratedNoteId(data.meetingNoteId);
    } catch (error) {
      notifyAndLogError('Generation failed', 'Could not start meeting note generation.', error);
    }
  };

  if (!afternoon) {
    return (
      <Alert color="orange" m="md">
        Could not find the afternoon tenant in the companies list.
      </Alert>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <Group justify="space-between" align="center">
        <Title order={3}>Transcript tester</Title>
        <Tooltip
          multiline
          w={320}
          label="Clears the form and deletes all transcript-tester data from the afternoon tenant (recordings, transcripts, generated notes and [test] templates). Real clients and imported templates are not affected."
        >
          <Button
            variant="outline"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleReset}
            loading={isCleaning}
          >
            Reset
          </Button>
        </Tooltip>
      </Group>

      <Group grow align="flex-start">
        <FileInput
          label="Audio recording"
          placeholder="Upload an audio file"
          accept=".mp3,.mp4,.wav,.aac,.flac,.ogg,.m4a,.mov"
          value={audioFile}
          onChange={setAudioFile}
        />
        <MultiSelect
          label="Client(s)"
          placeholder="Select afternoon client(s)"
          data={(clients ?? []).map(client => ({
            value: client.id,
            label: `${client.firstName} ${client.lastName}`,
          }))}
          value={selectedClientIds}
          onChange={setSelectedClientIds}
          searchable
        />
      </Group>

      <Text size="textSm" c="gray.8">
        Adviser:{' '}
        <Text span fw={FONT_WEIGHTS.medium} c="gray.10">
          {advisorName ?? '— (select a client)'}
        </Text>{' '}
        <Text span c="dimmed">
          (from client, read-only)
        </Text>
      </Text>

      <Stack gap="xs">
        <Title order={5}>Participants (ParticipantNames shape)</Title>
        <Group gap="xs">
          {advisorName && (
            <Badge color="orange" variant="light">
              {advisorName} · advisor
            </Badge>
          )}
          {selectedClients.map(client => (
            <Badge key={client.id} color="blue" variant="light">
              {client.firstName} {client.lastName} · client
            </Badge>
          ))}
          {guests.map((guest, index) => (
            <Badge
              key={`guest-${index}`}
              color="teal"
              variant="light"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="teal"
                  onClick={() => setGuests(current => current.filter((_, i) => i !== index))}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              }
            >
              {guest.name} · guest{guest.description ? ` (${guest.description})` : ''}
            </Badge>
          ))}
          {emails.map((email, index) => (
            <Badge
              key={`email-${index}`}
              color="grape"
              variant="light"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="grape"
                  onClick={() => setEmails(current => current.filter((_, i) => i !== index))}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              }
            >
              {email} · attendee email
            </Badge>
          ))}
          {familyMembers.map((member, index) => (
            <Badge
              key={`family-${index}`}
              color="cyan"
              variant="light"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="cyan"
                  onClick={() => setFamilyMembers(current => current.filter((_, i) => i !== index))}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              }
            >
              {member.name} · family{member.role ? ` (${member.role})` : ''}
            </Badge>
          ))}
        </Group>

        <Group gap="xs" align="flex-end">
          <TextInput
            label="Guest name"
            value={guestName}
            onChange={event => setGuestName(event.currentTarget.value)}
            w={180}
          />
          <TextInput
            label="Description"
            placeholder="e.g. spouse"
            value={guestDescription}
            onChange={event => setGuestDescription(event.currentTarget.value)}
            w={200}
          />
          <Button variant="light" onClick={addGuest} disabled={!guestName.trim()}>
            Add guest
          </Button>
        </Group>

        <Group gap="xs" align="flex-end">
          <TextInput
            label="Attendee email"
            value={emailInput}
            onChange={event => setEmailInput(event.currentTarget.value)}
            w={220}
          />
          <Button variant="light" onClick={addEmail} disabled={!emailInput.trim()}>
            Add email
          </Button>
          <TextInput
            label="Family member"
            value={familyName}
            onChange={event => setFamilyName(event.currentTarget.value)}
            w={180}
          />
          <TextInput
            label="Role"
            placeholder="e.g. son"
            value={familyRole}
            onChange={event => setFamilyRole(event.currentTarget.value)}
            w={140}
          />
          <Button variant="light" onClick={addFamilyMember} disabled={!familyName.trim()}>
            Add family
          </Button>
        </Group>
      </Stack>

      <Group>
        <Button onClick={handleProcess} disabled={!audioFile} loading={isProcessing}>
          Process (transcribe + clean)
        </Button>
      </Group>

      <Stack gap="xs">
        <Title order={5}>Meeting note template (any tenant)</Title>
        <Group grow align="flex-start">
          <Select
            label="Template tenant"
            placeholder="Select a tenant"
            data={sortedCompanies.map(company => ({
              value: company.id,
              label: `${company.name} (${company.tenantName})`,
            }))}
            value={templateCompanyId}
            onChange={value => {
              setTemplateCompanyId(value);
              setSelectedTemplateId(null);
            }}
            searchable
          />
          <Select
            label="Template"
            placeholder={templateCompanyId ? 'Select a template' : 'Select a tenant first'}
            data={meetingNoteTemplates.map(template => ({
              value: template.id,
              label: template.displayName,
            }))}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            disabled={!templateCompanyId}
            searchable
          />
        </Group>
        <Textarea
          label="Additional instructions"
          placeholder="Optional drafting guidance for the note (e.g. keep the tone warmer)"
          value={additionalInstructions}
          onChange={event => setAdditionalInstructions(event.currentTarget.value)}
          autosize
          minRows={2}
          maxLength={2000}
        />
        <Group>
          <Button onClick={handleGenerate} disabled={!canGenerate} loading={isGenerating}>
            Generate note
          </Button>
          {noteGenerating && (
            <Text size="textSm" c="dimmed">
              Generating meeting note…
            </Text>
          )}
          {noteFailed && (
            <Text size="textSm" c="red">
              Meeting note generation failed.
            </Text>
          )}
        </Group>
      </Stack>

      {(result || generatedNoteId) && (
        <>
          <Divider />
          <Tabs defaultValue="cleaned">
            <Tabs.List>
              <Tabs.Tab value="raw" disabled={!result}>
                Raw
              </Tabs.Tab>
              <Tabs.Tab value="cleaned" disabled={!result}>
                Cleaned
              </Tabs.Tab>
              <Tabs.Tab value="note" disabled={!generatedNoteId}>
                Meeting note
              </Tabs.Tab>
            </Tabs.List>
            <ScrollArea h="55vh">
              <Tabs.Panel value="raw">
                {result && <TranscriptView segments={result.rawSegments} />}
              </Tabs.Panel>
              <Tabs.Panel value="cleaned">
                {result && <TranscriptView segments={result.cleanedSegments} />}
              </Tabs.Panel>
              <Tabs.Panel value="note">{notePanelContent}</Tabs.Panel>
            </ScrollArea>
          </Tabs>
        </>
      )}
    </Stack>
  );
}
